import React, { useEffect, useState } from 'react';
import { localToday, dateStr } from '../utils/dates';
import { usePageMeta } from '../utils/pageMeta';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow:wght@300;400;600&display=swap');
  .tbv-page { all: initial; }
  .tbv-page *, .tbv-page *::before, .tbv-page *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .tbv-page {
    display: flex; flex-direction: column; align-items: center; min-height: 100vh;
    background: #0D1E35; color: white; font-family: 'Barlow', sans-serif;
    padding: 36px 16px 56px; overflow-x: hidden;
  }
  .tbv-header { display: flex; flex-direction: column; align-items: center; margin-bottom: 40px; animation: tbv-fadeDown 0.7s ease both; }
  .tbv-intro { max-width: 640px; width: 100%; text-align: center; font-weight: 300; font-size: clamp(0.92rem,1.7vw,1.06rem); line-height: 1.6; color: rgba(255,255,255,0.72); white-space: pre-line; margin: 0 0 32px; animation: tbv-fadeUp 0.55s 0.45s ease both; }
  .tbv-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; width: 100%; max-width: 860px; }
  @media (max-width: 700px) { .tbv-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; } }
  @media (max-width: 420px) { .tbv-grid { grid-template-columns: 1fr; gap: 10px; } }
  .tbv-tile {
    position: relative; background: rgba(255,255,255,0.06); border: 1px solid rgba(201,168,76,0.15);
    border-radius: 12px; padding: 14px 14px 12px; cursor: pointer; overflow: hidden;
    transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;
    display: flex; flex-direction: column; align-items: flex-start; gap: 8px;
    text-decoration: none; color: white;
  }
  .tbv-tile.tbv-future { opacity: 0.45; cursor: default; }
  .tbv-tile.tbv-admin-preview { border-color: rgba(201,168,76,0.4); }
  .tbv-tile.tbv-admin-preview::after { content: 'Admin preview'; position: absolute; top: 8px; right: 8px; background: #C9A84C; color: #0D1E35; font-size: 0.55rem; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; padding: 2px 6px; border-radius: 3px; }
  .tbv-tile:not(.tbv-future):hover { transform: translateY(-4px); border-color: rgba(201,168,76,0.5); box-shadow: 0 10px 32px rgba(0,0,0,0.4); }
  .tbv-tile::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg,rgba(201,168,76,0.08) 0%,transparent 60%); opacity: 0; transition: opacity 0.22s ease; }
  .tbv-tile:not(.tbv-future):hover::before { opacity: 1; }
  .tbv-tile:nth-child(1){animation:tbv-fadeUp .55s .08s ease both}
  .tbv-tile:nth-child(2){animation:tbv-fadeUp .55s .16s ease both}
  .tbv-tile:nth-child(3){animation:tbv-fadeUp .55s .24s ease both}
  .tbv-tile:nth-child(4){animation:tbv-fadeUp .55s .32s ease both}
  .tbv-tile:nth-child(5){animation:tbv-fadeUp .55s .40s ease both}
  .tbv-tile:nth-child(6){animation:tbv-fadeUp .55s .48s ease both}
  .tbv-icon { font-size: 1.9rem; line-height: 1; }
  .tbv-label { font-family: 'Bebas Neue', sans-serif; font-size: clamp(1.1rem,2.5vw,1.5rem); letter-spacing: 2px; line-height: 1.1; }
  .tbv-desc { font-size: 0.78rem; font-weight: 300; color: rgba(255,255,255,0.5); line-height: 1.45; }
  .tbv-badge { font-size: 0.6rem; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(201,168,76,0.7); border: 1px solid rgba(201,168,76,0.3); border-radius: 4px; padding: 2px 6px; display: inline-block; margin-top: 2px; }
  .tbv-arrow { margin-top: auto; width: 28px; height: 28px; border-radius: 50%; border: 1px solid rgba(201,168,76,0.3); display: flex; align-items: center; justify-content: center; color: #C9A84C; font-size: 0.9rem; transition: background 0.2s, border-color 0.2s; }
  .tbv-tile:not(.tbv-future):hover .tbv-arrow { background: rgba(201,168,76,0.15); border-color: #C9A84C; }
  .tbv-default-row { display: flex; align-items: center; gap: 10px; margin-top: 28px; animation: tbv-fadeUp 0.55s 0.56s ease both; }
  .tbv-checkbox { width: 16px; height: 16px; accent-color: #C9A84C; cursor: pointer; }
  .tbv-default-label { font-size: 0.78rem; color: rgba(255,255,255,0.4); letter-spacing: 0.5px; cursor: pointer; user-select: none; }
  .tbv-events { width: 100%; max-width: 860px; margin-top: 34px; animation: tbv-fadeUp 0.55s 0.5s ease both; }
  .tbv-events-title { font-family: 'Bebas Neue', sans-serif; font-size: 1.3rem; letter-spacing: 4px; color: rgba(201,168,76,0.85); margin-bottom: 12px; text-align: center; }
  .tbv-event-row {
    display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;
    padding: 10px 14px; border-radius: 10px; margin-bottom: 8px; cursor: pointer;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(201,168,76,0.15);
    transition: border-color 0.2s ease, background 0.2s ease;
  }
  .tbv-event-row:hover { border-color: rgba(201,168,76,0.5); background: rgba(201,168,76,0.07); }
  .tbv-event-row-special { background: rgba(201,168,76,0.10); border-color: rgba(201,168,76,0.35); }
  .tbv-event-badge {
    font-size: 0.56rem; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
    padding: 2px 7px; border-radius: 4px; white-space: nowrap; flex-shrink: 0;
  }
  .tbv-eb-tournament { background: #C9A84C; color: #0D1E35; }
  .tbv-eb-clinic { background: #8E44AD; color: white; }
  .tbv-eb-camp { background: #1ABC9C; color: #0D1E35; }
  .tbv-eb-class { border: 1px solid rgba(201,168,76,0.4); color: rgba(201,168,76,0.85); }
  .tbv-eb-openplay { border: 1px solid rgba(52,152,219,0.6); color: #5DADE2; }
  .tbv-eb-league { background: #3498DB; color: white; }
  .tbv-event-rowname { font-size: 0.82rem; font-weight: 600; color: white; }
  .tbv-event-rowmeta { font-size: 0.7rem; color: rgba(255,255,255,0.4); margin-left: auto; white-space: nowrap; }
  .tbv-news { width: 100%; max-width: 560px; margin-top: 40px; text-align: center; animation: tbv-fadeUp 0.55s 0.55s ease both; }
  .tbv-news-title { font-family: 'Bebas Neue', sans-serif; font-size: 1.3rem; letter-spacing: 4px; color: rgba(201,168,76,0.85); margin-bottom: 6px; }
  .tbv-news-sub { font-size: 0.78rem; font-weight: 300; color: rgba(255,255,255,0.5); line-height: 1.5; margin-bottom: 14px; }
  .tbv-news-form { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
  .tbv-news-input {
    flex: 1 1 180px; min-width: 0; padding: 10px 12px; border-radius: 8px;
    background: rgba(255,255,255,0.06); border: 1px solid rgba(201,168,76,0.3);
    color: white; font-family: 'Barlow', sans-serif; font-size: 0.85rem;
  }
  .tbv-news-input::placeholder { color: rgba(255,255,255,0.35); }
  .tbv-news-input:focus { outline: none; border-color: #C9A84C; }
  .tbv-news-btn {
    flex: 0 0 auto; padding: 10px 22px; border-radius: 8px; border: none; cursor: pointer;
    background: #C9A84C; color: #0D1E35; font-family: 'Barlow', sans-serif;
    font-weight: 600; font-size: 0.85rem; letter-spacing: 1px; text-transform: uppercase;
    transition: background 0.2s ease;
  }
  .tbv-news-btn:hover:not(:disabled) { background: #e0be6a; }
  .tbv-news-btn:disabled { opacity: 0.55; cursor: default; }
  .tbv-news-msg { font-size: 0.78rem; margin-top: 10px; line-height: 1.5; }
  .tbv-news-ok { color: #7FCB9B; }
  .tbv-news-err { color: #E28E82; }
  .tbv-footer { margin-top: 40px; text-align: center; font-size: 0.7rem; color: rgba(255,255,255,0.18); letter-spacing: 2px; text-transform: uppercase; animation: tbv-fadeUp 0.55s 0.6s ease both; }
  .tbv-sms-link { color: rgba(255,255,255,0.25); text-decoration: none; letter-spacing: 2px; font-size: 0.7rem; text-transform: uppercase; display: block; margin-bottom: 6px; }
  .tbv-sms-link:hover { color: rgba(201,168,76,0.6); }
  @keyframes tbv-fadeDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes tbv-fadeUp { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
`;

const DEFAULT_TILES = [
  { key: 'junior',      icon: '🏐', label: 'Junior Training',        desc: 'Skill development programs for youth athletes of all levels.',  path: '/junior-training',                       external: false, enabled: true,  image_data: '', image_mime: '' },
  { key: 'adult',       icon: '🌊', label: 'Adult Training',          desc: 'Competitive and recreational sessions for adult players.',       path: '/adult-training',                        external: false, enabled: true,  image_data: '', image_mime: '' },
  { key: 'tournaments', icon: '🏆', label: 'Tournament Registration', desc: 'Sign up for upcoming beach volleyball tournaments.',             path: '/tournaments',                           external: false, enabled: true,  image_data: '', image_mime: '' },
  { key: 'league',      icon: '🗓', label: 'League Registration',     desc: 'Join a recreational or competitive league.',                    path: '',                                       external: false, enabled: false, image_data: '', image_mime: '' },
  { key: 'membership',  icon: '⭐', label: 'Memberships',             desc: 'Flexible membership plans for regular adult players.',          path: '/adult-membership',                      external: false, enabled: true,  image_data: '', image_mime: '' },
  { key: 'streaming',   icon: '📺', label: 'Live Streaming',          desc: 'Watch live and on-demand tournament coverage.',                 path: 'https://www.sporfie.com/torchvolleyball', external: true,  enabled: true,  image_data: '', image_mime: '' },
];


// Map of tile keys to feature flag fields they're gated behind.
// Tiles listed here are hidden from non-admins until the flag flips on.
// Admins always see them, with a small "Admin preview" badge.
const GATED_TILES = {
  membership: 'membershipPagePublic',
};

function Landing() {
  const navigate = useNavigate();
  const [pubEvents, setPubEvents] = useState([]);

  // Public upcoming events: published tournaments/clinics/camps/tryouts, any
  // league open for registration or under way, junior and adult training
  // classes for the next week, plus standing adult open play (Tue/Thu 6-10 PM).
  // No personal data is ever read here.
  useEffect(() => {
    (async () => {
      const today = localToday();
      const items = [];
      const fmt = (ds) => {
        const d = new Date(ds + 'T12:00:00');
        return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
      };

      // Special events (publicly readable)
      try {
        const tSnap = await getDocs(query(collection(db, 'tournaments'), where('date', '>=', today), orderBy('date'), limit(12)));
        tSnap.docs.forEach(d => {
          const t = d.data();
          // Cancellation: status flags or the tournament_status field. Archived
          // (active:false) but not cancelled = removed (hide); cancelled stays
          // visible, marked, consistent with classes.
          const st = String(t.status || '').toLowerCase();
          const tSt = String(t.tournament_status || '').toLowerCase();
          const tCancelled = st === 'canceled' || st === 'cancelled' || t.canceled === true || t.cancelled === true ||
              tSt.startsWith('canceled') || tSt.startsWith('cancelled');
          if (!t.active && !tCancelled) return;
          if (t.publish_date && t.publish_date > today) return;
          const kind = ({ tournament: 'Tournament', clinic: 'Clinic', camp: 'Camp', tryout: 'Tryout', event: 'Event' })[t.eventType] || 'Tournament';
          items.push({
            kind, special: true, name: t.name, cancelled: tCancelled, sortDate: t.date,
            meta: `${fmt(t.date)}${t.location ? ' · ' + t.location : ''}`,
            path: '/tournaments',
          });
        });
      } catch (e) { console.warn('landing tournaments load failed:', e); }

      // Leagues. A season rather than a date, so the live statuses decide what
      // shows, not a date window — the same three the Leagues page uses. One
      // already under way sorts to today so a September start does not rank
      // above tomorrow and get cut by the slice.
      try {
        const lSnap = await getDocs(collection(db, 'leagues'));
        lSnap.docs.forEach(d => {
          const lg = d.data();
          if (!['registration', 'active', 'playoffs'].includes(lg.status)) return;
          const started = lg.startDate && lg.startDate <= today;
          items.push({
            kind: 'League', special: true, name: lg.name || 'League',
            sortDate: started ? today : (lg.startDate || today),
            meta: started
              ? (lg.status === 'playoffs' ? 'Playoffs' : 'In progress')
              : `${lg.startDate ? fmt(lg.startDate) : 'Dates TBA'}${lg.status === 'registration' ? ' · registration open' : ''}`,
            path: `/leagues/${d.id}`,
          });
        });
      } catch (e) { console.warn('landing leagues load failed:', e); }

      // Training classes: next 7 days
      try {
        const weekOut = dateStr(new Date(Date.now() + 7 * 86400000));
        const cSnap = await getDocs(query(collection(db, 'classes'), where('date', '>=', today), where('date', '<=', weekOut), orderBy('date'), limit(40)));
        cSnap.docs.forEach(d => {
          const c = d.data();
          const st = (c.sessionType || '').toLowerCase();
          const isJunior = st.startsWith('junior');
          const isAdult = st.startsWith('adult');
          if (!isJunior && !isAdult) return;
          const t12 = (t) => {
            if (!t) return '';
            const [h, m] = t.split(':').map(Number);
            const ap = h >= 12 ? 'PM' : 'AM';
            return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${ap}`;
          };
          items.push({
            kind: isJunior ? 'Junior Training' : 'Adult Training', special: false,
            name: c.eventName || (isJunior ? 'Junior Training' : 'Adult Training'),
            cancelled: c.status === 'canceled',
            sortDate: c.date,
            meta: `${fmt(c.date)}${c.startTime ? ' · ' + t12(c.startTime) : ''}`,
            path: isJunior ? '/junior-training' : '/adult-training',
          });
        });
      } catch (e) { console.warn('landing classes load failed:', e); }

      // Standing adult open play: every Tuesday and Thursday, 6-10 PM
      for (let i = 0; i < 8; i++) {
        const d = new Date(); d.setDate(d.getDate() + i);
        const dow = d.getDay();
        if (dow !== 2 && dow !== 4) continue;
        const ds = dateStr(d);
        items.push({
          kind: 'Open Play', special: false,
          name: 'Adult Open Play — AA/A',
          sortDate: ds,
          meta: `${fmt(ds)} · 6:00 – 10:00 PM`,
          path: '/adult-training',
        });
      }

      items.sort((a, b) => (a.sortDate > b.sortDate ? 1 : a.sortDate < b.sortDate ? -1 : (a.special ? -1 : 1)));
      // Reserve the cap for one-off events. Sorting by date alone let a week of
      // recurring classes and standing open play fill all 8 slots, so a
      // tournament three weeks out and every league fell off the end — the
      // things worth announcing were the first to go. Specials take what they
      // need, the routine fills what is left, and date order survives both.
      const LIMIT = 8;
      const specials = items.filter(i => i.special).slice(0, LIMIT);
      const routine = items.filter(i => !i.special).slice(0, Math.max(0, LIMIT - specials.length));
      const keep = new Set([...specials, ...routine]);
      setPubEvents(items.filter(i => keep.has(i)));
    })();
  }, []);
  usePageMeta('{club} | Orlando Beach Volleyball Club', 'Beach volleyball training, tournaments, memberships, and court rentals at {facility} in Orlando, FL. Junior and adult programs at our six-court private facility.');
  const [tiles, setTiles] = useState([]);
  const [tilesLoaded, setTilesLoaded] = useState(false);
  const [headerText, setHeaderText] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [featureFlags, setFeatureFlags] = useState({});

  useEffect(() => {
    document.title = 'Torch Beach Volleyball';
    // Load tiles from Firestore
    getDoc(doc(db, 'settings', 'dashboardTiles')).then(snap => {
  if (snap.exists() && snap.data().tiles?.length > 0) {
    setTiles(snap.data().tiles.map(t => ({ image_data: '', image_mime: '', ...t })));
  } else {
    setTiles(DEFAULT_TILES);
  }
  if (snap.exists()) setHeaderText(snap.data().headerText || '');
  setTilesLoaded(true);
}).catch(() => {
  setTiles(DEFAULT_TILES);
  setTilesLoaded(true);
});

    // Load feature flags (public read)
    getDoc(doc(db, 'settings', 'featureFlags')).then(snap => {
      console.log('[Landing] featureFlags exists?', snap.exists(), 'data:', snap.exists() ? snap.data() : null);
      if (snap.exists()) setFeatureFlags(snap.data() || {});
    }).catch(e => { console.error('[Landing] featureFlags read error:', e); /* fail closed — flags stay {} */ });

    // Resolve admin status (lets admins preview gated tiles)
    const unsub = onAuthStateChanged(auth, async (u) => {
      console.log('[Landing] auth state:', u ? `signed in as ${u.email} (uid ${u.uid})` : 'signed out');
      if (!u) { setIsAdmin(false); return; }
      try {
        const userDoc = await getDoc(doc(db, 'users', u.uid));
        const roles = userDoc.exists() ? (userDoc.data().roles || []) : [];
        console.log('[Landing] user doc exists?', userDoc.exists(), 'roles:', roles, 'isAdmin?', roles.includes('admin'));
        setIsAdmin(roles.includes('admin'));
      } catch (e) {
        console.error('[Landing] admin check error:', e);
      }
    });
    return unsub;
  }, []);

  // ── Newsletter signup ──────────────────────────────────────────────────────
  // Public: no account needed, which is the point — these are the people who
  // don't have one yet. The address goes to newsletterSubscribers and shows up
  // as its own audience in Content → Send Announcement.
  const [nlForm, setNlForm] = useState({ name: '', email: '', website: '' });
  const [nlSending, setNlSending] = useState(false);
  const [nlMsg, setNlMsg] = useState(null);   // { ok, text }

  const setNl = (k) => (e) => setNlForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSubscribe(e) {
    e.preventDefault();
    const name = nlForm.name.trim();
    const email = nlForm.email.trim();
    if (!name) { setNlMsg({ ok: false, text: 'Please enter your name.' }); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setNlMsg({ ok: false, text: 'Please enter a valid email address.' }); return;
    }
    setNlSending(true); setNlMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'subscribeNewsletter');
      const r = await fn({ name, email, website: nlForm.website });
      setNlForm({ name: '', email: '', website: '' });
      // An address that unsubscribed before stays suppressed — the form can't
      // undo that, so say so rather than promise email that will never arrive.
      setNlMsg(r.data?.optedOut
        ? { ok: false, text: 'That address previously unsubscribed from club email, so we’ve left it off. Email info@torchvolleyball.com to turn it back on.' }
        : { ok: true, text: r.data?.alreadySubscribed ? 'You’re already on the list — nothing to do.' : 'You’re on the list. Watch your inbox for club news.' });
    } catch (err) {
      console.warn('newsletter signup failed:', err);
      setNlMsg({ ok: false, text: err?.message || 'Something went wrong. Please try again.' });
    }
    setNlSending(false);
  }

  function handleTileClick(tile) {
    if (!tile.enabled || !tile.path) return;
    if (tile.external) {
      window.open(tile.path, '_blank');
    } else {
      navigate(tile.path);
    }
  }

  return (
    <>
      <style>{styles}</style>
      <div className="tbv-page">
        <header className="tbv-header">
          <img src="/horzlogo.png" alt="Torch Beach Volleyball"
               style={{ maxWidth: 320, width: '80vw', objectFit: 'contain' }} />
          <img src="/goldline1.png" alt=""
               style={{ maxWidth: 360, width: '85vw', objectFit: 'contain', marginTop: 10, opacity: 0.85 }} />
        </header>

        {headerText && (
          <div className="tbv-intro">{headerText}</div>
        )}
		
		{tilesLoaded && (
        <main className="tbv-grid">
          {tiles.map(tile => {
              const flagField = GATED_TILES[tile.key];
              // Tile is gated when it's listed in GATED_TILES and the flag is not yet public.
              const gated = !!flagField && featureFlags[flagField] !== true;
              // Admins always see gated tiles as active (preview). Non-admins see them disabled.
              const effectivelyEnabled = tile.enabled && (!gated || isAdmin);
              const isAdminPreview = gated && isAdmin && tile.enabled;
              const cls = `tbv-tile${!effectivelyEnabled ? ' tbv-future' : ''}${isAdminPreview ? ' tbv-admin-preview' : ''}`;
              return (
            <div key={tile.key}
                 className={cls}
                 onClick={() => effectivelyEnabled && handleTileClick(tile)}>
              {tile.image_data
                ? <img src={`data:${tile.image_mime};base64,${tile.image_data}`}
                       alt={tile.label}
                       style={{ width: 48, height: 48, objectFit: 'contain', marginBottom: 2 }} />
                : <div className="tbv-icon">{tile.icon}</div>
              }
              <div className="tbv-label">{tile.label}</div>
              <div className="tbv-desc">{tile.desc}</div>
              {!effectivelyEnabled && <span className="tbv-badge">Coming Soon</span>}
              {effectivelyEnabled && <div className="tbv-arrow">→</div>}
            </div>
              );
            })}
        </main>
		)}

        {pubEvents.length > 0 && (
          <section className="tbv-events">
            <div className="tbv-events-title">Upcoming Events</div>
            {pubEvents.map((ev, i) => (
              <div key={i}
                className={ev.special ? 'tbv-event-row tbv-event-row-special' : 'tbv-event-row'}
                onClick={() => navigate(ev.path)}>
                <span className={`tbv-event-badge ${
                  ev.kind === 'Tournament' || ev.kind === 'Event' ? 'tbv-eb-tournament'
                  : ev.kind === 'Clinic' ? 'tbv-eb-clinic'
                  : ev.kind === 'Camp' ? 'tbv-eb-camp'
                  : ev.kind === 'League' ? 'tbv-eb-league'
                  : ev.kind === 'Open Play' ? 'tbv-eb-openplay'
                  : 'tbv-eb-class'}`}>{ev.kind}</span>
                <span className="tbv-event-rowname" style={ev.cancelled ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>{ev.name}</span>
                {ev.cancelled && (
                  <span style={{ fontSize: '0.52rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', padding: '2px 6px', borderRadius: '4px', background: '#C0392B', color: 'white' }}>Canceled</span>
                )}
                <span className="tbv-event-rowmeta">{ev.meta}</span>
              </div>
            ))}
          </section>
        )}

        <section className="tbv-news">
          <div className="tbv-news-title">Club Newsletter</div>
          <div className="tbv-news-sub">
            Tournaments, leagues, camps and open play — sent occasionally, never shared.
            Every email has an unsubscribe link.
          </div>
          <form className="tbv-news-form" onSubmit={handleSubscribe}>
            <input className="tbv-news-input" type="text" placeholder="Your name"
                   value={nlForm.name} onChange={setNl('name')} autoComplete="name" />
            <input className="tbv-news-input" type="email" placeholder="Email address"
                   value={nlForm.email} onChange={setNl('email')} autoComplete="email" />
            {/* Honeypot — hidden from humans, bots fill it and get silently dropped */}
            <input type="text" name="website" value={nlForm.website} onChange={setNl('website')}
                   style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
                   tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <button className="tbv-news-btn" type="submit" disabled={nlSending}>
              {nlSending ? 'Signing up…' : 'Sign Up'}
            </button>
          </form>
          {nlMsg && (
            <div className={`tbv-news-msg ${nlMsg.ok ? 'tbv-news-ok' : 'tbv-news-err'}`}>{nlMsg.text}</div>
          )}
        </section>

        <footer className="tbv-footer">
          <a href="/login" className="tbv-sms-link">Admin Login</a>
          <a href="/sms-optin" className="tbv-sms-link">SMS Opt-In</a>
          <a href="/sms-terms" className="tbv-sms-link">SMS Terms</a>
          <a href="/privacy" className="tbv-sms-link">Privacy Policy</a>
          <a href="/terms" className="tbv-sms-link">Terms of Service</a>
          © {new Date().getFullYear()} Torch Beach Volleyball · Orlando, FL
        </footer>
      </div>
    </>
  );
}

export default Landing;
